import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AppModalShell, {
  AppModalButton,
  AppModalContentItem,
} from "../common/AppModalShell.jsx";
import { getSearchKeyIcon } from "../../constants/searchKeys";

const CONFETTI = Array.from({ length: 34 }, (_, index) => {
  const angle = ((index * 47) % 360) * (Math.PI / 180);
  const distance = 92 + ((index * 29) % 128);

  return {
    id: index,
    color: ["#7DD3FC", "#FDE68A", "#F9A8D4", "#A7F3D0", "#C4B5FD"][index % 5],
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance - 26,
    rotate: (index % 2 ? -1 : 1) * (140 + ((index * 37) % 220)),
    delay: (index % 8) * 0.025,
    width: index % 3 === 0 ? 5 : 7,
    height: index % 4 === 0 ? 13 : 8,
    radius: index % 3 === 0 ? "999px" : "2px",
  };
});

const REWARD_CONFIG = [
  {
    field: "coins",
    kind: "coins",
    icon: "/assets/vault/coin_icon.svg",
    iconClassName: "h-10 w-10",
  },
  {
    field: "diamonds",
    kind: "diamonds",
    icon: "/assets/vault/diamand_icon.svg",
    iconClassName: "h-10 w-10",
  },
  ...[1, 2, 3, 4, 5].map((level) => ({
    field: `keys_level_${level}`,
    kind: "key",
    level,
    icon: getSearchKeyIcon(level),
    iconClassName: "h-12 w-12 rotate-[45deg]",
  })),
];

const toWholeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : 0;
};

const formatNumber = (value) => new Intl.NumberFormat().format(toWholeNumber(value));

const formatCompactCount = (value) => {
  const numeric = toWholeNumber(value);
  if (numeric >= 1e9) return `${(numeric / 1e9).toFixed(1)}B`;
  if (numeric >= 1e6) return `${(numeric / 1e6).toFixed(1)}M`;
  if (numeric >= 1e3) return `${(numeric / 1e3).toFixed(1)}K`;
  return String(numeric);
};

const getAttendancePercent = (attendance) => {
  const explicitPercent = Number(attendance?.percent);
  if (Number.isFinite(explicitPercent)) {
    return Math.max(0, Math.min(100, Math.round(explicitPercent)));
  }

  const total = toWholeNumber(attendance?.total);
  const confirmed = toWholeNumber(attendance?.confirmed);
  if (!total) return 0;
  return Math.max(0, Math.min(100, Math.round((confirmed / total) * 100)));
};

const getAttendanceMinPercent = (attendance) => {
  const explicitPercent = Number(attendance?.min_percent);
  if (Number.isFinite(explicitPercent)) {
    return Math.max(0, Math.min(100, Math.round(explicitPercent)));
  }
  return 50;
};

function CelebrationConfetti({ runKey }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute left-1/2 top-[32%] h-0 w-0">
        {CONFETTI.map((piece) => (
          <motion.span
            key={`${runKey}-${piece.id}`}
            className="absolute left-0 top-0 block"
            style={{
              width: piece.width,
              height: piece.height,
              borderRadius: piece.radius,
              backgroundColor: piece.color,
              boxShadow: `0 0 8px ${piece.color}80`,
            }}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0.2, rotate: 0 }}
            animate={{
              x: piece.x,
              y: piece.y,
              opacity: [0, 1, 1, 0],
              scale: [0.2, 1, 0.86],
              rotate: piece.rotate,
            }}
            transition={{
              duration: 1.45,
              delay: piece.delay,
              ease: [0.18, 0.75, 0.32, 1],
            }}
          />
        ))}
      </div>
    </div>
  );
}

function AttendanceSummary({ attendance, t, success }) {
  const confirmed = toWholeNumber(attendance?.confirmed);
  const total = toWholeNumber(attendance?.total);
  const percent = getAttendancePercent(attendance);

  return (
    <AppModalContentItem className="mt-4">
      <div className="flex items-center justify-center gap-3">
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full border text-[18px] font-black leading-none tabular-nums ${
            success
              ? "border-emerald-100/24 bg-emerald-200/[0.1] text-emerald-50"
              : "border-amber-100/18 bg-amber-100/[0.08] text-amber-50"
          }`}
        >
          {percent}%
        </div>
        <div className="text-left">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/42">
            {t("host_completion_modal.attendance.label")}
          </p>
          <p className="mt-1 text-[17px] font-semibold leading-5 text-white/88">
            {t("host_completion_modal.attendance.confirmed", {
              confirmed: formatNumber(confirmed),
              total: formatNumber(total),
            })}
          </p>
        </div>
      </div>
    </AppModalContentItem>
  );
}

function RewardCards({ reward, t }) {
  const rewardItems = REWARD_CONFIG.map((config) => ({
    ...config,
    amount: toWholeNumber(reward?.[config.field]),
  })).filter((item) => item.amount > 0);

  if (!rewardItems.length) return null;

  return (
    <AppModalContentItem className="mt-4">
      <div className="flex w-full items-stretch justify-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {rewardItems.map((item) => (
          <div
            key={item.field}
            className="flex min-w-[92px] flex-1 items-center justify-center gap-1.5 rounded-[18px] bg-white/[0.06] px-2 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_10px_20px_rgba(0,0,0,0.12)]"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-[#0C1524]/68">
              <img
                src={item.icon}
                alt=""
                aria-hidden="true"
                className={item.kind === "coins" ? "h-7 w-7" : "h-8 w-8 rotate-[45deg]"}
              />
            </div>
            <p className="min-w-0 whitespace-nowrap text-[19px] font-extrabold leading-none text-white">
              {item.kind === "coins"
                ? `+${formatCompactCount(item.amount)}`
                : item.kind === "diamonds"
                ? `+${formatCompactCount(item.amount)}`
                : `×${formatCompactCount(item.amount)}`}
            </p>
          </div>
        ))}
      </div>
    </AppModalContentItem>
  );
}

function FailureTips({ t, tooFewPeople = false }) {
  const tipKeys = tooFewPeople
    ? ["invite_more", "publish_earlier"]
    : ["confirm_attendance", "publish_earlier"];

  return (
    <AppModalContentItem className="mt-6">
      <p className="text-center text-[11px] font-bold uppercase tracking-[0.16em] text-white/42">
        {t("host_completion_modal.failure.tips_title")}
      </p>
      <ul className="mt-3 space-y-3 text-left text-[15px] leading-6 text-white/74">
        {tipKeys.map((tipKey) => (
          <li key={tipKey} className="flex gap-2.5">
            <span className="mt-0.5 text-amber-200/80" aria-hidden="true">•</span>
            <span>{t(`host_completion_modal.failure.tips.${tipKey}`)}</span>
          </li>
        ))}
      </ul>
    </AppModalContentItem>
  );
}

/**
 * Standalone host-facing completion experience. The live trigger and reward
 * claiming are deliberately supplied by the caller; the sandbox uses mock data.
 */
export default function HostEventCompletionModal({
  isOpen,
  onClose,
  event = null,
  outcome = "success",
  attendance = null,
  reward = null,
  onClaimReward = null,
}) {
  const { t } = useTranslation("events");
  const success = outcome === "success";
  const [claiming, setClaiming] = useState(false);
  const [celebrationRun, setCelebrationRun] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setClaiming(false);
    setCelebrationRun((current) => current + 1);
  }, [event?.id, isOpen, outcome]);

  const eventName = event?.name || t("host_completion_modal.event_fallback");
  const attendancePercent = getAttendancePercent(attendance);
  const attendanceMinPercent = getAttendanceMinPercent(attendance);
  const externalParticipantCount = toWholeNumber(attendance?.total);
  const minimumExternalParticipantCount = toWholeNumber(attendance?.min_confirmed);
  const tooFewPeople =
    !success &&
    minimumExternalParticipantCount > 0 &&
    externalParticipantCount < minimumExternalParticipantCount;
  const hasReward = useMemo(
    () => REWARD_CONFIG.some((config) => toWholeNumber(reward?.[config.field]) > 0),
    [reward]
  );

  const handleClaim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const shouldClose = await onClaimReward?.(reward || {});
      if (shouldClose !== false) {
        onClose?.();
      }
    } finally {
      setClaiming(false);
    }
  };

  const footer = success ? (
    <AppModalButton
      onClick={!hasReward ? onClose : handleClaim}
      loading={claiming}
      className="bg-white text-[#123549]"
    >
      {!hasReward
        ? t("host_completion_modal.success.continue")
        : t("host_completion_modal.success.claim_button")}
    </AppModalButton>
  ) : (
    <AppModalButton onClick={onClose}>{t("host_completion_modal.failure.close_button")}</AppModalButton>
  );

  return (
    <AppModalShell
      open={Boolean(isOpen)}
      onClose={onClose}
      title={success ? t("host_completion_modal.success.title") : t("host_completion_modal.failure.title")}
      closeAriaLabel={t("host_completion_modal.close_aria")}
      maxWidthClassName="max-w-[480px]"
      titleClassName="text-[22px] sm:text-[24px]"
      panelClassName={success ? "shadow-[0_24px_80px_rgba(0,0,0,0.36),0_0_54px_rgba(74,222,128,0.12)]" : ""}
      footer={footer}
      overlayContent={success ? <CelebrationConfetti runKey={celebrationRun} /> : null}
    >
      <AppModalContentItem className="text-center">
        <div
          className={`mx-auto flex h-[76px] w-[76px] items-center justify-center rounded-[27px] border text-[38px] shadow-[0_16px_34px_rgba(0,0,0,0.2)] ${
            success
              ? "border-emerald-100/30 bg-[radial-gradient(circle_at_35%_20%,rgba(209,250,229,0.42),rgba(52,211,153,0.15)_52%,rgba(9,38,44,0.58))]"
              : "border-amber-100/20 bg-[radial-gradient(circle_at_35%_20%,rgba(254,243,199,0.23),rgba(180,83,9,0.12)_52%,rgba(42,25,20,0.56))]"
          }`}
          aria-hidden="true"
        >
          {success ? "🏆" : "😔"}
        </div>
        <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.13em] text-white/44">
          {t("host_completion_modal.event_label")}
        </p>
        <p className="mt-1 text-[18px] font-semibold text-white">{eventName}</p>
      </AppModalContentItem>

      {success ? (
        <>
          <AppModalContentItem className="mt-4 text-center text-[16px] font-medium leading-7 text-white/82">
            {t("host_completion_modal.success.description")}
          </AppModalContentItem>
          <AttendanceSummary attendance={attendance} t={t} success />
          <RewardCards reward={reward} t={t} />
        </>
      ) : (
        <>
          <AppModalContentItem className="mt-4 text-center text-[16px] font-medium leading-7 text-white/82">
            {t("host_completion_modal.failure.description")}
          </AppModalContentItem>
          <AttendanceSummary attendance={attendance} t={t} success={false} />
          <AppModalContentItem className="mt-5 text-center text-[15px] leading-6 text-amber-50/82">
            {tooFewPeople
              ? t("host_completion_modal.failure.reason_too_few_people", {
                  people: externalParticipantCount + 1,
                  minPeople: minimumExternalParticipantCount + 1,
                  minParticipants: minimumExternalParticipantCount,
                })
              : t("host_completion_modal.failure.reason", {
                  percent: attendancePercent,
                  minPercent: attendanceMinPercent,
                })}
          </AppModalContentItem>
          <FailureTips t={t} tooFewPeople={tooFewPeople} />
        </>
      )}
    </AppModalShell>
  );
}
